import React from 'react'

import DcFlexBox from '../dc-flex-box/dc-flex-box'
import DcText from '../dc-text/dc-text'
import { colors } from '../styleGuide'

export interface DemandForecastMetric {
  icon: React.ComponentType<{ color?: string; size?: number | string }>
  label: string
  value: string | number
  color: string
  bg: string
}

export interface WarehouseItem {
  warehouse_name: string
  stock: number
}

export interface DcDemandForecastCardProps {
  productName: string
  productSku: string
  productImage?: string
  leadTime: number
  moq: number
  leadTimeIcon: React.ComponentType<{ color?: string; size?: number | string }>
  moqIcon: React.ComponentType<{ color?: string; size?: number | string }>
  metrics: DemandForecastMetric[]
  warehouses?: WarehouseItem[]
  onClick?: () => void
  className?: string
  style?: React.CSSProperties
  width?: string
}

const MetricCard = ({
  icon: iconComponent,
  label,
  value,
  color,
  bg,
  compact = false,
}: {
  icon: React.ComponentType<{ color?: string; size?: number | string }>
  label: string
  value: string | number
  color: string
  bg: string
  compact?: boolean
}) => (
  <div
    style={{
      padding: compact ? '6px 8px' : '10px 12px',
      borderRadius: compact ? '8px' : '10px',
      border: '1px solid #e5e7eb',
      backgroundColor: bg,
      display: 'flex',
      alignItems: 'center',
      gap: compact ? 6 : 10,
      minHeight: compact ? 44 : 56,
    }}
  >
    <div
      style={{
        width: compact ? 24 : 30,
        height: compact ? 24 : 30,
        borderRadius: compact ? 6 : 8,
        backgroundColor: 'rgba(255,255,255,0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {React.createElement(iconComponent, {
        color,
        size: compact ? 16 : 18,
      })}
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <DcText
        style={{
          fontSize: compact ? '10px' : '11px',
          color: '#6b7280',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </DcText>
      <DcText
        style={{
          fontWeight: 600,
          fontSize: compact ? '12px' : '14px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </DcText>
    </div>
  </div>
)

export const DcDemandForecastCard: React.FC<DcDemandForecastCardProps> = ({
  productName,
  productSku,
  productImage,
  leadTime,
  moq,
  leadTimeIcon,
  moqIcon,
  metrics,
  warehouses,
  onClick,
  className,
  style,
  width = '49%',
}) => {
  return (
    <div
      className={className}
      onClick={onClick}
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '10px',
        padding: '12px',
        background: '#ffffff',
        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        transition: 'all 0.2s ease',
        cursor: onClick ? 'pointer' : 'default',
        width,
        ...style,
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow =
            '0 4px 12px rgba(15, 23, 42, 0.08)'
          e.currentTarget.style.transform = 'translateY(-2px)'
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(15, 23, 42, 0.04)'
          e.currentTarget.style.transform = 'translateY(0)'
        }
      }}
    >
      {/* Product Name and SKU */}
      <DcFlexBox
        alignItems="center"
        gap="8px"
        height="auto"
        style={{ marginBottom: '8px' }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: '8px',
            backgroundColor: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
          }}
        >
          {productImage ? (
            <img
              src={productImage}
              alt={productName}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
                const parent = target.parentElement
                if (parent) {
                  parent.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; font-size: 10px; color: #6b7280; text-align: center; padding: 4px;">${productName
                    .substring(0, 2)
                    .toUpperCase()}</div>`
                }
              }}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                fontSize: '12px',
                fontWeight: 600,
                color: '#6b7280',
                textAlign: 'center',
              }}
            >
              {productName.substring(0, 2).toUpperCase()}
            </div>
          )}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <DcText
            style={{
              fontWeight: 600,
              fontSize: '13px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginBottom: '2px',
            }}
          >
            {productName}
          </DcText>
          <DcText style={{ fontSize: '11px', color: '#6b7280' }}>
            SKU: {productSku}
          </DcText>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '8px',
            borderRadius: '8px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
            }}
          >
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: '8px',
                backgroundColor: 'rgba(14, 165, 233, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {React.createElement(leadTimeIcon, {
                color: '#0ea5e9',
                size: 16,
              })}
            </div>
            <div style={{ flex: 1 }}>
              <DcText
                style={{
                  fontSize: '10px',
                  color: '#6b7280',
                  fontWeight: 500,
                  marginBottom: '2px',
                }}
              >
                Lead Time
              </DcText>
              <DcText
                style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#0ea5e9',
                }}
              >
                {leadTime ?? 0}
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#6b7280',
                    marginLeft: '4px',
                  }}
                >
                  days
                </span>
              </DcText>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
            }}
          >
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: '8px',
                backgroundColor: colors.secondaryLight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {React.createElement(moqIcon, {
                color: colors.secondary,
                size: 16,
              })}
            </div>
            <div style={{ flex: 1 }}>
              <DcText
                style={{
                  fontSize: '10px',
                  color: '#6b7280',
                  fontWeight: 500,
                  marginBottom: '2px',
                }}
              >
                MOQ
              </DcText>
              <DcText
                style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: colors.secondary,
                }}
              >
                {moq ?? 0}
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#6b7280',
                    marginLeft: '4px',
                  }}
                >
                  units
                </span>
              </DcText>
            </div>
          </div>
        </div>
      </DcFlexBox>

      {/* Metrics Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 6,
        }}
      >
        {metrics.map((metric, index) => (
          <MetricCard
            key={index}
            icon={metric.icon}
            label={metric.label}
            value={metric.value}
            color={metric.color}
            bg={metric.bg}
            compact={true}
          />
        ))}
      </div>

      {/* Warehouse Stock Breakdown */}
      {warehouses && warehouses.length > 0 && (
        <div
          style={{
            marginTop: '8px',
            padding: '10px',
            borderRadius: '8px',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
          }}
        >
          <DcText
            style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '6px',
            }}
          >
            Warehouse Stock Breakdown:
          </DcText>
          <DcFlexBox flexDirection="row" flexWrap="wrap" gap="10px" height="auto">
            {warehouses.map((warehouse, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '6px 12px',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #f1f5f9',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                  minWidth: 'fit-content',
                }}
              >
                <DcText
                  style={{
                    fontSize: '11px',
                    color: '#64748b',
                    fontWeight: 500,
                  }}
                >
                  {warehouse.warehouse_name}
                </DcText>
                <div
                  style={{
                    height: '12px',
                    width: '1px',
                    backgroundColor: '#e2e8f0',
                  }}
                />
                <DcText
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#0f172a',
                  }}
                >
                  {warehouse.stock.toLocaleString()}
                </DcText>
              </div>
            ))}
          </DcFlexBox>
        </div>
      )}
    </div>
  )
}

export default DcDemandForecastCard

